import { formatConstant } from '@/modules/common/lib/string';
import { Button, Skeleton, Typography } from '@mui/material';
import { ComponentProps } from 'react';

type ButtonGroupNavigatorProps<T extends string> = {
  value: T;
  onChange: (value: T) => Promise<void>;
  items: T[];
  countsByItem?: Record<T, number>;
  isLoading?: boolean;
  renderLabel?: (item: T) => string;
} & Omit<ComponentProps<'div'>, 'onChange'>;

export function ButtonGroupNavigator<T extends string>({
  value,
  onChange,
  items,
  countsByItem,
  isLoading = false,
  renderLabel = (item: T) => formatConstant(item.toLowerCase()),
  ...props
}: ButtonGroupNavigatorProps<T>) {
  return (
    <div
      {...props}
      className={`py-4 flex gap-2 min-h-16 ${props.className ?? ''}`}
    >
      {items.map(item => (
        <Button
          key={item}
          variant="outlined"
          className={`flex-1 text-left ${
            value === item ? 'text-info border-info' : 'text-secondary'
          }`}
          onClick={() => onChange(item)}
        >
          <div className="flex flex-col px-2 py-3 w-full">
            <Typography
              variant="subtitle2"
              color="inherit"
              className="capitalize"
            >
              {renderLabel(item)}
            </Typography>
            {isLoading ? (
              <Skeleton variant="text" width={20} height={20} />
            ) : (
              <Typography variant="subtitle2" color="inherit">
                {countsByItem?.[item] ?? 0}
              </Typography>
            )}
          </div>
        </Button>
      ))}
    </div>
  );
}
